import React, { createContext, useEffect, useState } from 'react';
import { useAmplitudeExperiment } from '../utils/ampli';
import config from 'config';
import { selectUser } from '../reducers/user.selectors';
import { useSelector } from 'react-redux';
import useRouter from '../utils/useRouter';

interface AIAssistantContext {
  isAssistantAvailable: boolean,
  isAssistantOpen: boolean;
  toggleAssistant: VoidFunction
  assistantPosition: {x: number, y: number};
  setAssistantPosition: (position: {x: number, y: number}) => void;
  draggableBounds: {left: number, right: number, top: number, bottom: number};
  setDraggableBounds: (bounds: {left: number, right: number, top: number, bottom: number}) => void;
  isExtended: boolean
  toggleExtended: VoidFunction
}

export const aiAssistantContext = createContext<AIAssistantContext>({
  isAssistantAvailable: false,
  isAssistantOpen: false,
  toggleAssistant: () => {},
  assistantPosition: { x: 0, y: 0 },
  setAssistantPosition: () => {},
  draggableBounds: { left: 0, right: 0, top: 0, bottom: 0 },
  setDraggableBounds: () => {},
  isExtended: false,
  toggleExtended: () => false
});

const AIAssistantProvider: React.FC = ({ children }) => {
  const user = useSelector(selectUser);
  const { match: { location: { pathname } } } = useRouter();
  const [isAssistantAvailable, setIsAssistantAvailable] = useState(false);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [assistantPosition, setAssistantPosition] = useState({ x: 0, y: 0 });
  const [draggableBounds, setDraggableBounds] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  const [isExtended, setIsExtended] = useState(false);

  const { isReady, variant } = useAmplitudeExperiment(user, config.amplitudeExperiments.enableReaderAiAssistant);

  const toggleAssistant = () => {
    setIsAssistantOpen(!isAssistantOpen);
  };
  
  const toggleExtended = () => {
    setIsExtended(!isExtended);
  };

  useEffect(() => {
    const isAssistantEnabled = isReady && variant === 'on';
    setIsAssistantAvailable(isAssistantEnabled);
  }, [isReady, variant]);

  useEffect(() => {
    setIsAssistantOpen(false);
    setAssistantPosition({ x: 0, y: 0 });
    setDraggableBounds({ left: 0, right: 0, top: 0, bottom: 0 });
    setIsExtended(false);
  }, [pathname]);

  return ( 
    <aiAssistantContext.Provider
      value={{
        isAssistantAvailable,
        isAssistantOpen,
        toggleAssistant,
        assistantPosition,
        setAssistantPosition,
        draggableBounds,
        setDraggableBounds,
        isExtended,
        toggleExtended
      }}
    >
      {children}
    </aiAssistantContext.Provider>
  );
};

export default AIAssistantProvider;
